// eslint-disable-next-line no-unused-vars
import React, { useMemo, memo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { array, object, func, string, oneOfType, bool } from 'prop-types';
import { Button, Loading } from '@carbon/react';
import styled from '@emotion/styled';
import uniqueId from 'lodash/uniqueId';
import Carousel from 'react-multi-carousel';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Package, Gift, Boxes, Eye } from 'lucide-react';

import Img from 'Containers/Image/Image';
import { StyledProductListItem } from 'Components/Products/StyledProductItems';
import { PROMOTION_BNDL, PROMOTION_PRGF } from 'Constants';
import Price from 'Components/Price/Price';
import AddToCart from 'Components/AddToCart/AddToCart';

export const StyledPromotionsContainer = styled.div`
  .promoHeading {
    svg {
      margin-right: 10px;
      color: inherit;
    }

    height: 40px;
    padding: 0 15px;
    background-color: #F4F7FC;
    display: flex;
    align-items: center;
    font-size: 1rem;
    color: ${ props => props.theme.colors.darkGray };
  }

  border-bottom: 1px solid ${ props => props.theme.products.colors.border };

  .promotionContainer {
    align-items: center;

    &:last-of-type {
      border-bottom: 0;
    }
  }

  background-color: #FCFDFF;
`;

export const StyledPromotionListItem = styled(StyledProductListItem)`
  border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  flex-direction: row;
  min-height: 0;

  &[type='BNDL'] {
    .products {
      top: 12px;
    }
  }

  .products {
    position: relative;
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    flex-grow: 1;

    .promotionCarousel {
      margin-bottom: 1.5rem;

      + .react-multi-carousel-dot-list {
        .react-multi-carousel-dot--active {
          button {
            background-color: #FF8519;
          }
        }

        button {
          border: 0;
          min-width: 0;
          min-height: 0;
          width: 14px;
          height: 14px;
          padding: 0;
          margin: 0 5px;
          border-radius: 14px;
          background-color: #A8A8A8;
        }
      }
    }
  }

  .promoItem {
    &:not(:last-of-type):after {
      display: block;
      position: absolute;
      right: -3px;
      top: 50%;
      transform: translateY(-50%);
      content: '+';
      font-size: 3.5rem;
      font-weight: 600;
      color: ${ props => props.theme.products.colors.inStock };
    }
  }

  .priceContainer {
    border: 0;
    position: relative;

    &:before {
      position: absolute;
      left: -30px;
      top: 50%;
      padding-left: 10px;
      transform: translateY(-50%);
      display: block;
      content: '=';
      font-size: 3.5rem;
      line-height: 3rem;
      color: ${ props => props.theme.products.colors.inStock };
      background-color: #FCFDFF;
    }

    .addToCart {
      font-size: 1rem;
    }
  }
`;

export const StyledPromoProduct = styled.div`
  position: relative;
  padding: 0 15px;
  display: flex;
  align-items: center;
  //background-color: #FCFDFF;

  .preview {
    top: 0 !important;
    left: 0;
    min-height: 90px !important;
    min-width: 90px !important;
    max-width: 90px !important;
    max-height: 90px !important;
    margin: 0 10px 0 0 !important;
    border: 0 !important;
    position: relative;
    overflow: hidden;

    .giftIcon {
      background-color: rgba(255, 255, 255, 0.9);
      padding: 3px 5px;
      position: absolute;
      top: 0;
      left: 0;
    }

    svg {
      max-width: 55px !important;
    }

    .image {
      margin: 0 auto;
      width: auto;
      height: auto;
      max-width: 90px;
      max-height: 90px;
    }

    button {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 30px;
      height: 30px;
      min-height: 30px;
      min-width: 0;
      margin: 0;
      background-color: ${ props => props.theme.colors.white };
      border: 1px solid ${ props => props.theme.products.colors.border };
      border-radius: 2px;
      padding: 0;

      svg {
        margin: 0;
        stroke: #ff8519;
      }
    }
  }

  .details {
    font-size: 0.875rem;
    flex-direction: column !important;
    overflow: hidden;
    padding-right: 10px;

    .title {
      font-size: 0.875rem !important;
      line-height: 1.25rem !important;
      color: ${ props => props.theme.colors.darkGray };
      margin: 0;
      padding: 0;
    }

    .quantity {
      font-weight: 600;
      margin: 5px 0 2px;
    }

    .price {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: ${ props => props.theme.colors.default };

      .oldPrice {
        margin-right: 5px;
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: normal;
      }

      .currentPrice {
        font-size: 1rem;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .preview {
      button {
        display: none;
      }
    }
  }
`;

const ProductPromotions = memo((
  {
    type, onQuickView, loading: initialLoading = false, onAddToCart, product, promotions, withVat
  }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(initialLoading);

  const handleOnAddToCart = useCallback(
    async payload => {
      try {
        setLoading(payload.promotionId);
        await onAddToCart(payload);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const renderProduct = (promoProduct, promotion) => (
    <StyledPromoProduct
      key={ uniqueId(`${ promoProduct.productId }_${ promoProduct.ronPromoPrice }_${ promoProduct.minQuantity }`) }
      className="promoItem"
      theme={ theme }
    >
      <div className="preview">
        { promotion.type === PROMOTION_PRGF && !promoProduct.mainProduct && (
          <div className="giftIcon">
            <Gift />
          </div>
        ) }
        <Link
          to={ `/products/${ promoProduct.slug }/pd/${ promoProduct.productId }` }
          state={ {
            component: 'product_promotions',
            source: 'promotion_product_image',
            clickedOn: 'image',
            productId: promoProduct.productId
          } }
        >
          <Img
            className="image"
            src={ promoProduct?.pictures?.at(0)?.urlThumbnailPicture }
            unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
            loader={ <Loading active withOverlay={ false } small /> }
            loading="lazy"
            alt={ product.name }
          />
        </Link>
        <Button
          onClick={ () => onQuickView({
            ...promoProduct,
            id: promoProduct.productId,
            quantity: null,
            promoItem: true,
          }) }
          data-test="onQuickView"
          data-testid={ promoProduct.productId }
          kind="primary"
          className="green"
        >
          <Eye />
        </Button>
      </div>
      <div className="details">
        <Link
          className="title"
          to={ `/products/${ promoProduct.slug }/pd/${ promoProduct.productId }` }
          state={ {
            component: 'product_promotions',
            source: 'promotion_product_title',
            clickedOn: 'title',
            productId: promoProduct.productId
          } }
        >
          { promoProduct.title }
        </Link>
        <p className="quantity">
          { promoProduct.quantity }
          &nbsp;
          { promoProduct.quantity === 1
            ? t('products.product')
            : t('products.products')
          }
        </p>
        {
          promoProduct.totalPrice === 0
            ? <p className="price">{ t('products.free') }</p>
            : (
              <Price
                price={ promoProduct.price * promoProduct.quantity }
                promoPrice={ promoProduct.totalPrice }
                withVat={ withVat }
                displayVatInfo
              />
            )
        }
      </div>
    </StyledPromoProduct>
  );

  const renderPromo = promotion => {
    const mainPromoProduct = promotion.products
      .find(promoProd => promoProd.mainProductId === (product.id || product.productId));
    return (
      <StyledPromotionListItem
        key={ uniqueId(promotion.promotionId) }
        theme={ theme }
        type={ promotion.type }
        className="promotionContainer"
      >
        <div className="products">
          { promotion.type === PROMOTION_BNDL && (promotion.products || []).length > 3
            ? (
              <Carousel
                className="promotionCarousel"
                ssr
                deviceType="desktop"
                arrows={ false }
                showDots
                renderDotsOutside
                itemClass="promoItem"
                infinite={ false }
                responsive={ {
                  largeDesktop: {
                    breakpoint: { max: 5000, min: 1750 },
                    items: 4,
                  },
                  desktop: {
                    breakpoint: { max: 1749, min: 1372 },
                    items: 3,
                  },
                  tablet: {
                    breakpoint: { max: 1371, min: 1024 },
                    items: 2,
                  },
                  mobile: {
                    breakpoint: { max: 1023, min: 0 },
                    items: 1,
                  },
                } }
              >
                { (promotion.products || []).map(prod => renderProduct(prod, promotion)) }
              </Carousel>
            )
            : (promotion.products || []).map(prod => renderProduct(prod, promotion))
          }
        </div>
        <div className="priceContainer">
          <Price
            price={ promotion.totalPrice }
            promoPrice={ promotion.totalPromoPrice }
            discountAmount={ promotion.totalPrice - promotion.totalPromoPrice }
            withVat={ withVat }
            displayVatInfo
          />
          <AddToCart
            id={ promotion.promotionId }
            size="lg"
            loading={ loading === promotion.promotionId }
            disabled={
              loading === promotion.promotionId
              || (promotion.products || [])
                .filter(prod => prod.minQuantity > prod.stockValue || !prod.stockValue)
                .length > 0
            }
            onClick={ () => handleOnAddToCart({
              ...(mainPromoProduct || product),
              // hardcode to not show confirmation modal for promotions since there are no supplier products here
              hasPrice: true,
              id: (
                mainPromoProduct?.mainProductId
                || mainPromoProduct?.productId
                || product.mainProductId
                || product.productId
                || product.id
              ),
              promotionId: promotion.promotionId,
            }) }
            trackingContext={ {
              location: 'product_promotions',
              product_sku: product?.code,
            } }
            showSelector={ false }
          />
        </div>
      </StyledPromotionListItem>
    );
  };

  const memoizedPromotions = useMemo(
    () => Object.values(promotions || {}).map(item => {
      const updatedItem = { ...item };
      if (type === 'N+1' && Array.isArray(updatedItem.products)) {
        updatedItem.products = [...updatedItem.products].reverse();
      }
      return renderPromo(updatedItem);
    }),
    [promotions, loading, product, type]
  );

  return (
    <StyledPromotionsContainer
      key={ uniqueId(type) }
      theme={ theme }
      type={ type }
      className="promotionsContainer"
    >
      <div className="promoHeading">
        <Boxes />
        <p
          dangerouslySetInnerHTML={ {
            __html: t(`products.promotionData.type.${ type }`),
          } }
        />
      </div>
      { memoizedPromotions }
    </StyledPromotionsContainer>
  );
});

ProductPromotions.propTypes = {
  type: string,
  loading: oneOfType([string, bool]),
  onAddToCart: func,
  onQuickView: func,
  promotions: oneOfType([object, array]),
  product: object,
  withVat: bool,
};

ProductPromotions.displayName = 'ProductPromotions';

export default ProductPromotions;
